import React, { useState, useEffect } from "react";
import { Check, X, Info, Mail, Star, Printer } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";

import { HomeSizeGuideModal } from "./HomeSizeGuideModal";

const AnimatedPrice = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(value);
  
  useEffect(() => {
    let start = displayValue;
    const end = value;
    if (start === end) return;
    
    const duration = 400;
    const startTime = performance.now();
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * ease;
      
      setDisplayValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(end);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);
  
  return <>{Math.round(displayValue)}</>;
};

interface ComparisonItem {
  task: string;
  description: string;
  standard: boolean;
  deep: boolean;
  moveOut: boolean;
}

interface ComparisonCategory {
  category: string;
  items: ComparisonItem[];
}

const comparisonData: ComparisonCategory[] = [
  {
    category: "Kitchen",
    items: [
      { task: "Clear cobwebs & dust high corners", description: "Removal of cobwebs from ceilings and high corners.", standard: true, deep: true, moveOut: true },
      { task: "Dust ceiling fans & light fixtures", description: "Careful dusting of accessible fans and fixtures.", standard: true, deep: true, moveOut: true },
      { task: "Wipe light switches & door handles", description: "Sanitizing frequently touched points.", standard: true, deep: true, moveOut: true },
      { task: "Dust window sills & ledges", description: "Removal of dust and debris from sills.", standard: true, deep: true, moveOut: true },
      { task: "Clean & polish sink", description: "Scrubbing and shining the kitchen sink.", standard: true, deep: true, moveOut: true },
      { task: "Wipe countertops & backsplash", description: "Cleaning and sanitizing all counter surfaces.", standard: true, deep: true, moveOut: true },
      { task: "Clean stovetop & burners", description: "Removal of grease and food spills.", standard: true, deep: true, moveOut: true },
      { task: "Clean inside & outside of microwave", description: "Thorough cleaning of microwave interior and exterior.", standard: true, deep: true, moveOut: true },
      { task: "Wipe exterior of all appliances", description: "Cleaning the outside of fridge, oven, and dishwasher.", standard: true, deep: true, moveOut: true },
      { task: "Vacuum & mop floors", description: "Thorough vacuuming and wet mopping of hard floors.", standard: true, deep: true, moveOut: true },
      { task: "Empty trash & replace liner", description: "Disposal of trash and replacing the bag.", standard: true, deep: true, moveOut: true },
      { task: "Dry dust blinds & shutters", description: "Light dusting of window treatments.", standard: true, deep: true, moveOut: true },
      { task: "Clean interior windows", description: "Wiping interior glass for a streak-free shine.", standard: false, deep: true, moveOut: true },
      { task: "Wipe doors & door frames", description: "Removing fingerprints and smudges from doors.", standard: false, deep: true, moveOut: true },
      { task: "Detail clean baseboards", description: "Hand-wiping baseboards to remove built-up dust.", standard: false, deep: true, moveOut: true },
      { task: "Clean exterior of cabinets & drawers", description: "Wiping down cabinet fronts to remove drips.", standard: false, deep: true, moveOut: true },
      { task: "Clean range hood exterior", description: "Removing grease from the outside of the range hood.", standard: false, deep: true, moveOut: true },
      { task: "Clean inside empty cabinets & drawers", description: "Vacuuming and wiping the interior (must be empty).", standard: false, deep: false, moveOut: true },
      { task: "Clean inside refrigerator", description: "Wiping shelves and drawers (must be mostly empty).", standard: false, deep: false, moveOut: true },
      { task: "Clean inside oven", description: "Removing baked-on grease and carbon.", standard: false, deep: false, moveOut: true },
      { task: "Sweep pantry & wipe empty shelves", description: "Cleaning the pantry area (must be empty).", standard: false, deep: false, moveOut: true },
      { task: "Wet wipe blinds & shutters", description: "Detailed wet cleaning of window treatments.", standard: false, deep: false, moveOut: true },
      { task: "Clean window tracks", description: "Removing dirt and debris from window tracks.", standard: false, deep: false, moveOut: true },
    ]
  },
  {
    category: "Bathrooms",
    items: [
      { task: "Clear cobwebs & dust high corners", description: "Removal of cobwebs from ceilings.", standard: true, deep: true, moveOut: true },
      { task: "Dust ceiling fans & light fixtures", description: "Dusting accessible fixtures.", standard: true, deep: true, moveOut: true },
      { task: "Wipe light switches & door handles", description: "Sanitizing touch points.", standard: true, deep: true, moveOut: true },
      { task: "Dust window sills & ledges", description: "Removing dust from sills.", standard: true, deep: true, moveOut: true },
      { task: "Clean & sanitize sink", description: "Thorough scrubbing of the bathroom sink.", standard: true, deep: true, moveOut: true },
      { task: "Wipe countertops & backsplash", description: "Sanitizing bathroom counters.", standard: true, deep: true, moveOut: true },
      { task: "Clean & polish mirrors", description: "Streak-free mirror cleaning.", standard: true, deep: true, moveOut: true },
      { task: "Scrub tub & shower walls", description: "Removing soap scum and buildup.", standard: true, deep: true, moveOut: true },
      { task: "Clean shower glass & tracks", description: "Cleaning glass doors and their tracks.", standard: true, deep: true, moveOut: true },
      { task: "Clean & sanitize toilet (inside/out)", description: "Complete cleaning and disinfection of the toilet.", standard: true, deep: true, moveOut: true },
      { task: "Vacuum & mop floors", description: "Cleaning bathroom floors.", standard: true, deep: true, moveOut: true },
      { task: "Empty trash & replace liner", description: "Disposal of bathroom waste.", standard: true, deep: true, moveOut: true },
      { task: "Dry dust blinds & shutters", description: "Light dusting of window coverings.", standard: true, deep: true, moveOut: true },
      { task: "Clean interior windows", description: "Wiping the inside of bathroom windows.", standard: false, deep: true, moveOut: true },
      { task: "Wipe doors & door frames", description: "Removing smudges from doors.", standard: false, deep: true, moveOut: true },
      { task: "Detail clean baseboards", description: "Hand-wiping baseboards.", standard: false, deep: true, moveOut: true },
      { task: "Clean exterior of cabinets & drawers", description: "Wiping vanity cabinet fronts.", standard: false, deep: true, moveOut: true },
      { task: "Clean inside empty cabinets & drawers", description: "Wiping interior of vanity (must be empty).", standard: false, deep: false, moveOut: true },
      { task: "Wet wipe blinds & shutters", description: "Detailed wet cleaning of blinds.", standard: false, deep: false, moveOut: true },
    ]
  },
  {
    category: "Bedrooms & Living Areas",
    items: [
      { task: "Clear cobwebs & dust high corners", description: "Removal of cobwebs from ceilings.", standard: true, deep: true, moveOut: true },
      { task: "Dust ceiling fans & light fixtures", description: "Dusting accessible fans and fixtures.", standard: true, deep: true, moveOut: true },
      { task: "Wipe light switches & door handles", description: "Sanitizing touch points.", standard: true, deep: true, moveOut: true },
      { task: "Dust window sills & ledges", description: "Removing dust from sills.", standard: true, deep: true, moveOut: true },
      { task: "Dust furniture & decor", description: "General dusting of surfaces and items.", standard: true, deep: true, moveOut: true },
      { task: "Clean & polish mirrors", description: "Streak-free mirror cleaning.", standard: true, deep: true, moveOut: true },
      { task: "Make beds (change linens if left out)", description: "Making the bed or changing sheets if provided.", standard: true, deep: true, moveOut: true },
      { task: "General tidying & pillow arranging", description: "Straightening up the living areas.", standard: true, deep: true, moveOut: true },
      { task: "Vacuum & mop floors", description: "Cleaning all accessible floors.", standard: true, deep: true, moveOut: true },
      { task: "Empty trash & replace liner", description: "Disposal of waste.", standard: true, deep: true, moveOut: true },
      { task: "Dry dust blinds & shutters", description: "Light dusting of window coverings.", standard: true, deep: true, moveOut: true },
      { task: "Clean interior windows", description: "Wiping the inside of windows.", standard: false, deep: true, moveOut: true },
      { task: "Wipe doors & door frames", description: "Removing smudges from doors.", standard: false, deep: true, moveOut: true },
      { task: "Detail clean baseboards", description: "Hand-wiping baseboards.", standard: false, deep: true, moveOut: true },
      { task: "Clean exterior of cabinets & drawers", description: "Wiping built-in cabinet fronts.", standard: false, deep: true, moveOut: true },
      { task: "Clean inside empty cabinets & drawers", description: "Wiping interior of built-ins (must be empty).", standard: false, deep: false, moveOut: true },
      { task: "Clean empty closet shelves & floors", description: "Wiping shelves and vacuuming closet floors.", standard: false, deep: false, moveOut: true },
      { task: "Wet wipe blinds & shutters", description: "Detailed wet cleaning of blinds.", standard: false, deep: false, moveOut: true },
    ]
  }
];

import { getPricesBySqft } from "@/lib/pricing";

export const PlanComparison = () => {
  const [isRecurring, setIsRecurring] = useState(false);
  const [sqft, setSqft] = useState(1500);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  const [compareMode, setCompareMode] = useState<"all" | "standard-deep">("all");
  const [email, setEmail] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const prices = getPricesBySqft(sqft);
  const standardPrice = prices.standard;
  const deepPrice = prices.deep;
  const moveOutPrice = prices.move;

  const handleSaveComparison = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Plan Comparison Save Form",
      formData: {
        email: email,
        sqft: sqft,
        isRecurring: isRecurring,
      },
      formLabels: {
        email: "Email Address",
        sqft: "Square Footage",
        isRecurring: "Is Recurring"
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
      locationId: "M7JmlFFXdtFNLwyfY61D",
      sessionId: crypto.randomUUID(),
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    setIsDialogOpen(false);
    toast({
      title: "Comparison Saved!",
      description: `We've sent the pricing details for your ${sqft} sq ft home to ${email}.`,
    });
    setEmail("");
  };

  return (
    <div className="w-full relative">
      <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-8 bg-muted/20 p-6 rounded-2xl border border-border relative">
        <div className="absolute top-4 right-4">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Save Comparison</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Your Quote</DialogTitle>
                <DialogDescription>
                  Enter your email to receive a detailed breakdown of these cleaning packages for your {sqft} sq ft home.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSaveComparison} className="flex flex-col gap-4 mt-4">
                <Input 
                  type="email" 
                  placeholder="Enter your email address" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <div className="flex flex-col gap-2">
                  <Button type="submit" className="w-full">Send to Email</Button>
                  <Button type="button" variant="outline" className="w-full" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" /> Print Comparison
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="w-full max-w-sm mt-8 md:mt-0">

          <div className="flex justify-between items-center mb-4">
            <Label className="text-lg font-bold text-foreground">Square Footage</Label>
            <span className="text-primary font-bold">{sqft > 5500 ? "5500+" : sqft} sq ft</span>
          </div>
          <div className="flex gap-4 items-center mb-4">
            <Input 
              type="number"
              value={sqft}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val >= 0) setSqft(val);
              }}
              className="w-28"
              min={500}
              max={6000}
              step={100}
            />
            <span className="text-sm text-muted-foreground">sq ft</span>
          </div>
          <Slider 
            value={[sqft]} 
            onValueChange={(val) => setSqft(val[0])} 
            max={6000} 
            min={500} 
            step={100} 
            aria-label="Square footage"
            className="cursor-pointer mb-2"
          />
          <div className="flex flex-col mt-2 gap-2">
            <HomeSizeGuideModal />
            <a href="https://www.zillow.com/" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-md hover:bg-primary/20 transition-colors flex items-center justify-center gap-1.5 w-fit shadow-sm">
              Unsure? Look up on Zillow ↗
            </a>
          </div>
        </div>

        <div className="hidden md:block w-px h-24 bg-border"></div>

          <div className="flex justify-center items-center gap-4">
            <Label 
              className={`text-lg cursor-pointer transition-colors ${!isRecurring ? 'font-bold text-primary' : 'text-muted-foreground'}`}
              onClick={() => setIsRecurring(false)}
            >
              One-Time
            </Label>
            <Switch 
              checked={isRecurring} 
              onCheckedChange={setIsRecurring} 
              className="data-[state=checked]:bg-primary"
            />
            <Label 
              className={`text-lg cursor-pointer transition-colors ${isRecurring ? 'font-bold text-primary' : 'text-muted-foreground'}`}
              onClick={() => setIsRecurring(true)}
            >
              Recurring <span className="text-sm font-normal text-green-500 ml-1">(Save 20-35%)</span>
            </Label>
          </div>
      </div>

      {/* Summary Widget */}
      <div className="mb-8 bg-muted/20 border border-border shadow-sm p-6 rounded-2xl max-w-sm mx-auto">
        <div className="text-lg font-bold text-foreground mb-4 text-center">Current Selection Summary</div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted-foreground">Home Size:</span>
          <span className="font-bold text-primary text-base">{sqft > 5500 ? "5500+" : sqft} sq ft</span>
        </div>
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-border/50">
          <span className="text-sm text-muted-foreground">Frequency:</span>
          <span className="font-bold text-base">{isRecurring ? "Recurring" : "One-Time"}</span>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Standard (55pt):</span>
            <span className="font-bold text-base">{sqft > 5500 ? "Custom" : `$${Math.round(isRecurring ? standardPrice * 0.8 : standardPrice)}`}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Deep Clean (75pt):</span>
            <span className="font-bold text-base text-primary">{sqft > 5500 ? "Custom" : `$${Math.round(isRecurring ? deepPrice * 0.8 : deepPrice)}`}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Move In/Out (90pt):</span>
            <span className="font-bold text-base text-accent">{sqft > 5500 ? "Custom" : `$${moveOutPrice}`}</span>
          </div>
        </div>
      </div>

      <TooltipProvider delayDuration={300}>
        <div className="hidden md:block overflow-x-auto pb-4 pt-10">
          <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[40%] font-bold text-foreground align-bottom pb-4">Cleaning Task</TableHead>
                <TableHead className="text-center pb-4 relative">
                  {sqft <= 2000 && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap shadow-md z-10">
                      <Star className="w-3 h-3 fill-current" /> Popular Choice
                    </div>
                  )}
                  <div className="font-bold text-foreground text-lg">Standard (55pt)</div>
                  <div className="text-sm font-normal text-muted-foreground mt-1">
                    {sqft > 5500 ? "Custom Quote" : <>From $<AnimatedPrice value={isRecurring ? standardPrice * 0.8 : standardPrice} /></>}
                  </div>
                </TableHead>
                <TableHead className="text-center pb-4 relative">
                  {sqft > 2000 && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap shadow-md z-10">
                      <Star className="w-3 h-3 fill-current" /> Popular Choice
                    </div>
                  )}
                  <div className="font-bold text-primary text-lg">Deep Clean (75pt)</div>
                  <div className="text-sm font-normal text-primary/70 mt-1">
                    {sqft > 5500 ? "Custom Quote" : <>From $<AnimatedPrice value={isRecurring ? deepPrice * 0.8 : deepPrice} /></>}
                  </div>
                </TableHead>
                <TableHead className="text-center pb-4">
                  <div className="font-bold text-accent text-lg">Move In/Out (90pt)</div>
                  <div className="text-sm font-normal text-accent/70 mt-1">
                    {isRecurring ? "One-time only" : sqft > 5500 ? "Custom Quote" : <span>From $<AnimatedPrice value={moveOutPrice} /></span>}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((cat, i) => (
                <React.Fragment key={i}>
                  <TableRow className="bg-muted/50 hover:bg-muted/50 border-y-2 border-border/50">
                    <TableCell colSpan={4} className="font-black text-primary py-4 uppercase tracking-widest text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-4 bg-primary rounded-full" />
                        {cat.category}
                      </div>
                    </TableCell>
                  </TableRow>
                  {cat.items.map((item, j) => (
                    <TableRow key={`${i}-${j}`} className={`transition-colors group ${item.standard !== item.deep || item.deep !== item.moveOut ? 'hover:bg-primary/10' : 'hover:bg-muted/20'}`}>
                      <TableCell className="font-medium text-foreground py-4">
                        <Tooltip>
                          <TooltipTrigger className="flex items-center gap-2 text-left cursor-help hover:text-primary transition-colors">
                            <span className="underline decoration-dotted underline-offset-4">{item.task}</span>
                            <Info className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[250px] text-sm font-normal">
                            {item.description}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-center py-3">
                        {item.standard ? (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mx-auto shadow-sm">
                            <Check className="h-5 w-5 text-primary stroke-[3px]" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                            <X className="h-5 w-5 text-muted-foreground/70 stroke-[3px]" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center py-3 bg-primary/5">
                        {item.deep ? (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mx-auto shadow-sm">
                            <Check className="h-5 w-5 text-primary stroke-[3px]" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                            <X className="h-5 w-5 text-muted-foreground/70 stroke-[3px]" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center py-3 bg-accent/5">
                        {item.moveOut ? (
                          <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center mx-auto shadow-sm">
                            <Check className="h-5 w-5 text-accent stroke-[3px]" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                            <X className="h-5 w-5 text-muted-foreground/70 stroke-[3px]" />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden pt-8">
          <Tabs defaultValue="standard" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 h-auto p-1 bg-muted/50">
              <TabsTrigger value="standard" className="flex flex-col py-3 px-1 text-xs whitespace-normal h-auto data-[state=active]:shadow-sm">
                <span className="font-bold text-sm">Standard</span>
                <span className="font-normal opacity-80 mt-1">{sqft > 5500 ? "Custom" : `$${Math.round(isRecurring ? standardPrice * 0.8 : standardPrice)}`}</span>
              </TabsTrigger>
              <TabsTrigger value="deep" className="flex flex-col py-3 px-1 text-xs whitespace-normal h-auto data-[state=active]:text-primary data-[state=active]:shadow-sm">
                <span className="font-bold text-sm">Deep Clean</span>
                <span className="font-normal opacity-80 mt-1">{sqft > 5500 ? "Custom" : `$${Math.round(isRecurring ? deepPrice * 0.8 : deepPrice)}`}</span>
              </TabsTrigger>
              <TabsTrigger value="moveout" className="flex flex-col py-3 px-1 text-xs whitespace-normal h-auto data-[state=active]:text-accent data-[state=active]:shadow-sm">
                <span className="font-bold text-sm">Move In/Out</span>
                <span className="font-normal opacity-80 mt-1">{sqft > 5500 ? "Custom" : `$${moveOutPrice}`}</span>
              </TabsTrigger>
            </TabsList>
            
            {["standard", "deep", "moveout"].map((planType) => (
              <TabsContent key={planType} value={planType} className="space-y-6 focus-visible:outline-none focus-visible:ring-0">
                {comparisonData.map((cat, i) => {
                  const includedItems = cat.items.filter(item => 
                    (planType === 'standard' && item.standard) || 
                    (planType === 'deep' && item.deep) || 
                    (planType === 'moveout' && item.moveOut)
                  );
                  
                  const excludedItems = cat.items.filter(item => 
                    (planType === 'standard' && !item.standard) || 
                    (planType === 'deep' && !item.deep) || 
                    (planType === 'moveout' && !item.moveOut)
                  );

                  return (
                    <div key={i} className="bg-card border rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-muted/30 p-3 border-b font-bold text-primary flex items-center gap-2 text-sm uppercase tracking-wider">
                        <div className="w-1 h-4 bg-primary rounded-full" />
                        {cat.category}
                      </div>
                      <div className="p-0 divide-y divide-border/50">
                        {includedItems.map((item, j) => (
                          <div key={`inc-${j}`} className="p-4 flex items-start gap-3 bg-background">
                            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <div>
                              <div className="font-medium text-sm text-foreground leading-tight mb-1">{item.task}</div>
                              <div className="text-xs text-muted-foreground">{item.description}</div>
                            </div>
                          </div>
                        ))}
                        {excludedItems.map((item, j) => (
                          <div key={`exc-${j}`} className="p-4 flex items-start gap-3 bg-muted/10 opacity-60">
                            <X className="h-5 w-5 text-muted-foreground/70 shrink-0 mt-0.5 stroke-[3px]" />
                            <div>
                              <div className="font-medium text-sm text-muted-foreground line-through decoration-muted-foreground/30 leading-tight mb-1">{item.task}</div>
                              <div className="text-xs text-muted-foreground/70">{item.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div className="pt-4 pb-8 sticky bottom-4 z-10">
                  <Button asChild className="w-full h-14 text-lg font-bold rounded-xl shadow-xl">
                    <a href={`/book?service=${planType === 'standard' ? 'standard' : planType === 'deep' ? 'deep-clean' : 'move-in-out'}`}>
                      Book {planType === 'standard' ? 'Standard' : planType === 'deep' ? 'Deep' : 'Move In/Out'} Clean
                    </a>
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </TooltipProvider>

    </div>
  );
};
